import { Close } from '@mui/icons-material';
import {
    CircularProgress,
    DialogTitle, Fade, IconButton, Dialog as MuiDialog, DialogProps as MuiDialogProps
} from '@mui/material';
import clsx from 'clsx';
import { ReactNode, useContext, useEffect } from 'react';

import { PageMessage, PageMessageContext } from '../PageMessage/PageMessage';

import styles from './Dialog.module.scss';


export interface DialogProps extends Omit<MuiDialogProps, 'title'> {
    title: ReactNode;
    loading?: boolean;
    classes?: MuiDialogProps['classes'] & {
        closeButton?: string;
    };
}

export function Dialog({
    children, title, onClose, open, loading, classes, ...props
}: DialogProps) {
    const { setDialogOpen, setPageMessage } = useContext(PageMessageContext);

    useEffect(() => {
        setDialogOpen(open);

        setPageMessage(undefined);

        return () => setDialogOpen(false); // set dialogOpen to false when Dialog unmounts
    }, [
        open, setDialogOpen, setPageMessage
    ]);

    const { closeButton: closeButtonClassName, ...dialogClasses } = classes || {};

    return (
        <MuiDialog
            {...props}
            classes={dialogClasses}
            open={open}
            onClose={(event, reason) => {
                if (reason === 'escapeKeyDown') {
                    onClose?.(event, reason);
                }
            }}
        >
            <DialogTitle
                className={clsx(styles.title, {
                    [styles.noOnClose]: !onClose
                })}
            >
                {title}

                {onClose && (
                    <IconButton
                        aria-label="close"
                        className={closeButtonClassName}
                        onClick={() => onClose?.({}, 'escapeKeyDown')}
                    >
                        <Close />
                    </IconButton>
                )}
            </DialogTitle>

            {loading ? (
                <div className={styles.loader}>
                    <Fade
                        in
                        enter
                        timeout={1000}
                    >
                        <CircularProgress />
                    </Fade>
                </div>
            ) : children}

            <PageMessage
                dialog
                className={styles.pageMessage}
            />
        </MuiDialog>
    );
}
